import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ErrorMessage from './ErrorMessage';
import { Spin, TreeSelect } from 'antd';
import { CaretDownOutlined, LoadingOutlined } from '@ant-design/icons';
import { useFormContext } from 'react-hook-form';
import debounce from 'lodash/debounce';
import { normalizePortalText } from 'utils/baochauTheme';
import { getSelectOptionLabel, getSelectOptionValue, getSelectValidation, toPlainSelectValue } from './selectHelpers';
import { getFormPopupContainer } from './popupHelpers';

const TreeSelectStyle = styled(TreeSelect)`
  display: flex;

  .ant-select-selector {
    min-height: 52px !important;
    padding: 0 16px !important;
    margin: 0 !important;
    border: 1px solid #d7dee8 !important;
    border-radius: 18px !important;
    background: #ffffff !important;
    box-shadow: none !important;
    align-items: center;
  }

  .ant-select-selector {
    font-size: 15px !important;
    width: 100%;
  }
  .ant-select-arrow .anticon:not(.ant-select-suffix) {
    pointer-events: none;
  }

  .ant-select-arrow {
    right: 16px;
    color: #667085;
  }

  .ant-select-selection-search {
    width: 100%;
    inset-inline-start: 0 !important;
    inset-inline-end: 0 !important;
  }

  .ant-select-selection-placeholder {
    color: #98a2b3 !important;
    line-height: 50px !important;
  }

  .ant-select-selection-item {
    color: #101828 !important;
    line-height: 50px !important;
  }

  &.is-invalid .ant-select-selector {
    border-color: #fda29b !important;
    box-shadow: 0 0 0 4px rgba(240, 68, 56, 0.08) !important;
  }

  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #f2b3ad !important;
    box-shadow: 0 0 0 4px rgba(237, 50, 55, 0.08) !important;
  }
`;

const normalizeTreeNode = (node) => {
  if (!node || typeof node !== 'object' || React.isValidElement(node)) {
    return node;
  }

  const value = getSelectOptionValue(node);
  const title = node.title ?? getSelectOptionLabel(node, value);
  const children = Array.isArray(node.children) ? node.children.map(normalizeTreeNode) : node.children;
  const hasLeafFlag = node.isLeaf !== undefined && node.isLeaf !== null && node.isLeaf !== '';
  const isLeaf = hasLeafFlag
    ? node.isLeaf === true || node.isLeaf === 1 || node.isLeaf === '1'
    : node.isLeaf;

  return {
    ...node,
    id: node.id ?? value,
    pId: node.pId ?? node.parent_id ?? 0,
    key: node.key ?? value,
    value,
    title,
    isLeaf,
    children,
  };
};

const normalizeTreeData = (data = []) => (Array.isArray(data) ? data.map(normalizeTreeNode) : []);

const normalizeText = (value) =>
  `${value || ''}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const hasTreeNodeValue = (nodes = [], value) =>
  (Array.isArray(nodes) ? nodes : []).some((node) => {
    if (String(getSelectOptionValue(node)) === String(value)) return true;
    return hasTreeNodeValue(node?.children, value);
  });

const FormTreeSelect = ({
  field,
  validation,
  bordered,
  fetchOptions,
  debounceTimeout,
  placeholder,
  dropdownStyle,
  popupClassName,
  dropdownClassName,
  classNames,
  styles,
  multiple,
  treeCheckable,
  value,
  onChange,
  getPopupContainer = getFormPopupContainer,
  ...props
}) => {
  const methods = useFormContext();
  const [treeData, setTreeData] = useState([]);
  const [searchTreeData, setSearchTreeData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const isMultiple = multiple || treeCheckable;
  const selectValidation = useMemo(() => getSelectValidation(validation, isMultiple), [validation, isMultiple]);

  useEffect(() => {
    methods?.register(field, selectValidation);
  }, [methods, field, selectValidation]);

  useEffect(() => {
    fetchOptions({ parent_id: 0 }).then((res) => setTreeData(normalizeTreeData(res)));
  }, [fetchOptions]);

  const loadData = ({ id, value }) => {
    const parent_id = id ?? value;

    return fetchOptions({ parent_id }).then((options) => {
      setTreeData((previousTreeData) => previousTreeData.concat(normalizeTreeData(options)));
    });
  };

  const loadSearchOptions = useCallback(
    (keyword) => {
      const search = `${keyword || ''}`.trim();
      setSearchValue(search);

      if (!search) {
        setSearchTreeData([]);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      Promise.resolve(fetchOptions({ search, limit: 50 }))
        .then((options) => {
          setSearchTreeData(normalizeTreeData(options));
        })
        .catch(() => {
          setSearchTreeData([]);
        })
        .finally(() => {
          setSearchLoading(false);
        });
    },
    [fetchOptions],
  );

  const debounceSearch = useMemo(() => debounce(loadSearchOptions, debounceTimeout), [debounceTimeout, loadSearchOptions]);

  useEffect(() => {
    return () => debounceSearch.cancel();
  }, [debounceSearch]);

  const filterTreeNode = useCallback((inputValue, treeNode = {}) => {
    const keyword = normalizeText(inputValue);
    if (!keyword) return true;

    const title = normalizeText(treeNode.title ?? treeNode.label ?? treeNode.name);
    const value = normalizeText(treeNode.value ?? treeNode.id);

    return title.includes(keyword) || value.includes(keyword);
  }, []);

  const popupRootClassName = [classNames?.popup?.root, popupClassName, dropdownClassName].filter(Boolean).join(' ');
  const popupStyles = {
    ...styles,
    popup: {
      ...styles?.popup,
      root: { ...dropdownStyle, ...styles?.popup?.root },
    },
  };
  const popupClassNames = {
    ...classNames,
    popup: {
      ...classNames?.popup,
      root: popupRootClassName || undefined,
    },
  };
  const fieldValue = methods.watch(field);
  const selectedValue = useMemo(
    () => toPlainSelectValue(fieldValue, isMultiple),
    [fieldValue, isMultiple],
  );
  const selectedTreeNode = useMemo(() => {
    const selectedNodeSource = value || fieldValue;
    if (
      !selectedNodeSource ||
      Array.isArray(selectedNodeSource) ||
      typeof selectedNodeSource !== 'object' ||
      React.isValidElement(selectedNodeSource)
    )
      return null;
    const node = normalizeTreeNode(selectedNodeSource);
    return getSelectOptionValue(node) ? node : null;
  }, [fieldValue, value]);
  const displayTreeData = useMemo(() => {
    const selectedNodeValue = getSelectOptionValue(selectedTreeNode);
    const optionSource = searchValue ? searchTreeData : treeData;
    if (!selectedNodeValue || hasTreeNodeValue(optionSource, selectedNodeValue)) return optionSource;
    return [selectedTreeNode, ...optionSource];
  }, [searchTreeData, searchValue, selectedTreeNode, treeData]);

  return (
    <React.Fragment>
      <TreeSelectStyle
        {...props}
        className={methods.formState.errors[field] ? 'is-invalid' : ''}
        value={selectedValue}
        suffixIcon={searchLoading ? <LoadingOutlined /> : <CaretDownOutlined />}
        styles={popupStyles}
        classNames={popupClassNames}
        placeholder={typeof placeholder === 'string' ? normalizePortalText(placeholder) : placeholder}
        onChange={(e, label, extra) => {
          methods.clearErrors(field);
          methods.setValue(field, e);
          setSearchValue('');
          setSearchTreeData([]);
          onChange?.(e, label, extra);
        }}
        loadData={loadData}
        showSearch
        filterTreeNode={filterTreeNode}
        onSearch={debounceSearch}
        multiple={multiple}
        treeCheckable={treeCheckable}
        treeData={displayTreeData}
        getPopupContainer={getPopupContainer}
        notFoundContent={searchLoading ? <Spin size='small' /> : 'Không tìm thấy dữ liệu'}
      />
      {methods.formState.errors[field] && <ErrorMessage message={methods.formState.errors[field]?.message} />}
    </React.Fragment>
  );
};

FormTreeSelect.propTypes = {
  field: PropTypes.string.isRequired,
  validation: PropTypes.object,
  bordered: PropTypes.bool,
  fetchOptions: PropTypes.func,
  debounceTimeout: PropTypes.number,
  getPopupContainer: PropTypes.func,
};

FormTreeSelect.defaultProps = {
  field: '',
  bordered: false,
  fetchOptions: () => {},
  debounceTimeout: 500,
  dropdownStyle: {
    maxHeight: 400,
    overflow: 'auto',
  },
  placeholder: '--Chọn--',
};

export default FormTreeSelect;
